import { useState, useCallback } from "react";
import { ToggleMinimizeSvg } from "../../../../../CommonComponents/svgs/Svgs";
import { FullScreenSvg, CompactSvg, SyncSvg } from "./components/controls/ThermalHeatMapSvgs";
import CustomToolTip from "../../../../../CommonComponents/CustomToolTip";
import ThermalHeatMap3dView from "./components/visualization/ThermalHeatMap3dView";
import ThermalHeatMapChart from "./components/charts/ThermalHeatMapChart";
import ThermalHeatMapSummary from "./components/panels/ThermalHeatMapSummary";
import { DEFAULT_THRESHOLDS, ThresholdConfig } from "./components/panels/ThermalAlertThresholds";
import { useThermalAlerts } from "./hooks/useThermalAlerts";
import useThermalHeatmap from "../../../hooks/useThermalHeatmap";
import "./styles/ThermalHeatMap.scss";
import {
  useThermalHeatMapState,
} from "./hooks/ThermalHeatMapHooks";

export type { FaceId, HeatmapRow } from "./hooks/ThermalHeatMapHooks";
export { formatTime } from "./hooks/ThermalHeatMapHooks";

const clearAlertSelection = (setActiveThresholds: any, setSelectedNode: any, setSelectedBreach: any) => {
  setActiveThresholds(null);
  setSelectedNode(null);
  setSelectedBreach(null);
};

const findNodeMatchingTemp = (face: string, peakTemp: number | undefined, heatmapRows: any[], nodesPerFace: number): number | null => {
  if (!peakTemp || heatmapRows.length === 0) return null;
  const row = heatmapRows.find(r => r.time_sec === heatmapRows[0].time_sec);
  if (!row) return null;
  for (let n = 1; n <= nodesPerFace; n++) {
    if (row.nodes[`${face}_N${n}`] === peakTemp) return n;
  }
  return null;
};

const ThermalHeatMap = () => {
  const {
    isThermalMonitor,
    setIsThermalOpen,
    isMinimized,
    setIsMinimized,
    isCompact,
    isFullscreen,
    isSynced,
    is3dView,
    setIs3dView,
    heatmapRows,
    nodesPerFace,
    stepIndex,
    isPlaying,
    playbackSpeed,
    fetchStatus,
    isLastStep,
    handleToggleCompact,
    handleToggleSync,
    handleToggleFullscreen,
    handleTogglePlay,
    handleCycleSpeed,
    summary,
    internalSummary,
    globalMin,
    globalMax,
    summaryItems,
    envParams,
    t0Epoch,
  } = useThermalHeatMapState();

  const [thresholds, setThresholds] = useState<ThresholdConfig[]>(DEFAULT_THRESHOLDS);
  const [visibleFaces, setVisibleFaces] = useState<string[] | null>(null);
  const [stepIndexOverride, setStepIndexOverride] = useState<number | null>(null);
  const [activeThresholds, setActiveThresholds] = useState<ThresholdConfig[] | null>(null);
  const [selectedNode, setSelectedNode] = useState<number | null>(null);
  const [selectedBreach, setSelectedBreach] = useState<"high" | "low" | null>(null);
  const { thermalModel } = useThermalHeatmap();
  const { alerts, isLoading: isAlertsLoading, evaluate } = useThermalAlerts(heatmapRows, thresholds, nodesPerFace, t0Epoch, thermalModel);

  const handleToggle2d3d = useCallback(() => setIs3dView(false), []);

  const handleAlertClick = (alert: any) => {
    setVisibleFaces([alert.face]);
    setActiveThresholds(thresholds.map(t => ({ ...t, enabled: t.id === alert.thresholdId })));
    setSelectedNode(findNodeMatchingTemp(alert.face, alert.peakTemp, heatmapRows, nodesPerFace));
    setSelectedBreach(alert.breach);

    if (heatmapRows.length > 0) {
      let idx = heatmapRows.findIndex(r => r.time_sec >= alert.timeSec);
      setStepIndexOverride(idx === -1 ? heatmapRows.length - 1 : idx);
    }
  };

  const handleChartFaceChange = (faces: string[]) => {
    setVisibleFaces(faces);
    clearAlertSelection(setActiveThresholds, setSelectedNode, setSelectedBreach);
  };

  const handleThresholdsChange = (t: ThresholdConfig[]) => {
    setThresholds(t);
    clearAlertSelection(setActiveThresholds, setSelectedNode, setSelectedBreach);
  };

  return (
    <>
      {isThermalMonitor && (
        <div className={`thermal-heatmap-monitor-container${isFullscreen ? " fullscreen" : isCompact ? " compact" : ""}`}> 
          <div className="thermal-heatmap-monitor-header">
            <span>Thermal Monitor</span>
            <div className="thermal-heatmap-monitor-header-buttons">
              <CustomToolTip title={isCompact ? "Expand view" : "Compact view"} placement="top">
                <button onClick={handleToggleCompact}>
                  <CompactSvg active={isCompact} />
                </button>
              </CustomToolTip>
              <CustomToolTip title={isFullscreen ? "Exit fullscreen" : "Fullscreen"} placement="top">
                <button onClick={handleToggleFullscreen}>
                  <FullScreenSvg active={isFullscreen} />
                </button>
              </CustomToolTip>
              <CustomToolTip title={isMinimized ? "Maximize" : "Minimize"} placement="top">
                <button onClick={() => setIsMinimized(!isMinimized)}>
                  <ToggleMinimizeSvg type={isMinimized ? "maximize" : "minimize"} />
                </button>
              </CustomToolTip>
            </div>
          </div>

          {!isMinimized && (
            <div className="thermal-heatmap-monitor-body">
              {fetchStatus === "loading" && (
                <div className="thermal-heatmap-empty-state">
                  <span>Loading thermal data...</span>
                </div>
              )}
              {fetchStatus === "unavailable" && (
                <div className="thermal-heatmap-empty-state">
                  <span>Thermal data unavailable for this simulation.</span>
                </div>
              )}
              {fetchStatus === "idle" && (
                <div className="thermal-heatmap-empty-state">
                  <span>Waiting for simulation data...</span>
                </div>
              )}

              {heatmapRows.length > 0 && is3dView && (
                <ThermalHeatMap3dView
                  heatmapRows={heatmapRows}
                  stepIndex={stepIndex}
                  nodesPerFace={nodesPerFace}
                  globalMin={globalMin!}
                  globalMax={globalMax!}
                  isPlaying={isPlaying}
                  isLastStep={isLastStep}
                  playbackSpeed={playbackSpeed}
                  isSynced={isSynced}
                  viewMode={isFullscreen ? "fullscreen" : isCompact ? "compact" : "normal"}
                  onToggleSync={handleToggleSync}
                  onTogglePlay={handleTogglePlay}
                  onCycleSpeed={handleCycleSpeed}
                  onToggle2d3d={handleToggle2d3d}
                />
              )}

              {heatmapRows.length > 0 && !is3dView && (
                <ThermalHeatMapChart
                  heatmapRows={heatmapRows}
                  stepIndex={stepIndexOverride !== null ? stepIndexOverride : stepIndex}
                  nodesPerFace={nodesPerFace}
                  thresholds={activeThresholds || thresholds}
                  onToggle3d={() => setIs3dView(true)}
                  isFullscreen={isFullscreen}
                  visibleFaces={visibleFaces}
                  onVisibleFacesChange={handleChartFaceChange}
                  alerts={alerts}
                  selectedNode={selectedNode}
                  selectedBreach={selectedBreach}
                />
              )}

              {heatmapRows.length > 0 && !isCompact && (
                <ThermalHeatMapSummary
                  isFullscreen={isFullscreen}
                  isCompact={isCompact}
                  summaryItems={summaryItems}
                  summary={summary}
                  internalSummary={internalSummary}
                  envParams={envParams}
                  heatmapRows={heatmapRows}
                  stepIndex={stepIndexOverride !== null ? stepIndexOverride : stepIndex}
                  stepIndexOverride={stepIndexOverride}
                  setStepIndexOverride={setStepIndexOverride}
                  alerts={alerts}
                  isAlertsLoading={isAlertsLoading}
                  thermalModel={thermalModel}
                  thresholds={thresholds}
                  activeThresholds={activeThresholds}
                  onThresholdsChange={handleThresholdsChange}
                  onEvaluateThresholds={evaluate}
                  onAlertClick={handleAlertClick}
                />
              )}
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default ThermalHeatMap;
